/**
 * Gate-area icon set — Feather-style stroke glyphs drawn with plain Views.
 *
 * NOTE(deviation, flagged): the brief asks for Feather from '@expo/vector-icons',
 * but that package is NOT installed in this workspace (checked
 * apps/resident/node_modules, the hoisted root node_modules and the lockfile)
 * and adding npm packages is forbidden. These are faithful geometric ports of
 * the design's 24x24 / 1.75px-stroke Lucide glyphs (design/source/icons.jsx),
 * cross-platform on iOS/Android/web. Swap one-for-one with Feather once the
 * package lands: <Icon name="check-circle" /> → <Feather name="check-circle" />.
 */
import React from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';

export type IconName =
  | 'user' | 'users' | 'check' | 'check-circle' | 'x' | 'plus' | 'filter'
  | 'share' | 'shield' | 'info' | 'clock' | 'calendar' | 'refresh' | 'package'
  | 'car' | 'alert-triangle' | 'phone' | 'map-pin' | 'list' | 'more-horizontal'
  | 'download' | 'chevron-left' | 'chevron-right' | 'copy' | 'mail';

type G = { s: number; sw: number; c: string };

/** Rounded stroke segment centred at (cx,cy) in the 24-grid, rotated `deg`. */
function Bar({ g, cx, cy, len, deg = 0, vertical = false }: {
  g: G; cx: number; cy: number; len: number; deg?: number; vertical?: boolean;
}) {
  const { s, sw, c } = g;
  const w = vertical ? sw : len * s;
  const h = vertical ? len * s : sw;
  return (
    <View
      style={{
        position: 'absolute',
        left: cx * s - w / 2,
        top: cy * s - h / 2,
        width: w,
        height: h,
        borderRadius: sw / 2,
        backgroundColor: c,
        transform: deg ? [{ rotate: `${deg}deg` }] : undefined,
      }}
    />
  );
}

/** Stroke circle centred at (cx,cy), diameter d (24-grid units). */
function Ring({ g, cx, cy, d, gapTop = false, rotate = 0 }: {
  g: G; cx: number; cy: number; d: number; gapTop?: boolean; rotate?: number;
}) {
  const { s, sw, c } = g;
  const px = d * s;
  return (
    <View
      style={{
        position: 'absolute',
        left: cx * s - px / 2,
        top: cy * s - px / 2,
        width: px,
        height: px,
        borderRadius: px / 2,
        borderWidth: sw,
        borderColor: c,
        borderTopColor: gapTop ? 'transparent' : c,
        transform: rotate ? [{ rotate: `${rotate}deg` }] : undefined,
      }}
    />
  );
}

function Dot({ g, cx, cy, d }: { g: G; cx: number; cy: number; d: number }) {
  const { s, c } = g;
  const px = Math.max(d * s, 2);
  return (
    <View style={{
      position: 'absolute', left: cx * s - px / 2, top: cy * s - px / 2,
      width: px, height: px, borderRadius: px / 2, backgroundColor: c,
    }} />
  );
}

/** Stroke rounded-rect; corner radii in grid units. */
function Box({ g, x, y, w, h, r = 2, style }: {
  g: G; x: number; y: number; w: number; h: number; r?: number; style?: ViewStyle;
}) {
  const { s, sw, c } = g;
  return (
    <View
      style={[{
        position: 'absolute', left: x * s, top: y * s,
        width: w * s, height: h * s,
        borderWidth: sw, borderColor: c, borderRadius: r * s,
      }, style]}
    />
  );
}

/** Top half of a circle (dome) — used for user shoulders. */
function Dome({ g, cx, topY, w }: { g: G; cx: number; topY: number; w: number }) {
  const { s, sw, c } = g;
  const wp = w * s;
  return (
    <View style={{
      position: 'absolute', left: cx * s - wp / 2, top: topY * s,
      width: wp, height: wp / 2 + sw, overflow: 'hidden',
    }}>
      <View style={{
        width: wp, height: wp, borderRadius: wp / 2, borderWidth: sw, borderColor: c,
      }} />
    </View>
  );
}

function Check({ g, cx = 12, cy = 12, k = 1 }: { g: G; cx?: number; cy?: number; k?: number }) {
  return (
    <>
      <Bar g={g} cx={cx - 4.4 * k} cy={cy + 1.6 * k} len={6.4 * k} deg={45} />
      <Bar g={g} cx={cx + 1.9 * k} cy={cy + 0.4 * k} len={11.4 * k} deg={-45} />
    </>
  );
}

function glyph(name: IconName, g: G): React.ReactNode {
  switch (name) {
    case 'user':
      return (<>
        <Ring g={g} cx={12} cy={8} d={7.5} />
        <Dome g={g} cx={12} topY={14.5} w={15} />
      </>);
    case 'users':
      return (<>
        <Ring g={g} cx={9} cy={7.5} d={7} />
        <Dome g={g} cx={9} topY={13.5} w={13.5} />
        <Ring g={g} cx={17.5} cy={8.5} d={5} />
        <Dome g={g} cx={18.5} topY={14.5} w={9} />
      </>);
    case 'check':
      return <Check g={g} />;
    case 'check-circle':
      return (<>
        <Ring g={g} cx={12} cy={12} d={19} />
        <Check g={g} cy={12.4} k={0.62} />
      </>);
    case 'x':
      return (<>
        <Bar g={g} cx={12} cy={12} len={16} deg={45} />
        <Bar g={g} cx={12} cy={12} len={16} deg={-45} />
      </>);
    case 'plus':
      return (<>
        <Bar g={g} cx={12} cy={12} len={14} />
        <Bar g={g} cx={12} cy={12} len={14} vertical />
      </>);
    case 'filter':
      return (<>
        <Bar g={g} cx={12} cy={7} len={16} />
        <Bar g={g} cx={12} cy={12} len={10} />
        <Bar g={g} cx={12} cy={17} len={4.5} />
      </>);
    case 'share':
      return (<>
        <Box g={g} x={4.5} y={11} w={15} h={9.5} r={2}
          style={{ borderTopWidth: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }} />
        <Bar g={g} cx={4.5 + g.sw / (2 * g.s)} cy={12} len={2.4} vertical />
        <Bar g={g} cx={19.5 - g.sw / (2 * g.s)} cy={12} len={2.4} vertical />
        <Bar g={g} cx={12} cy={8} len={10} vertical />
        <Bar g={g} cx={10} cy={4.9} len={5.4} deg={-45} />
        <Bar g={g} cx={14} cy={4.9} len={5.4} deg={45} />
      </>);
    case 'shield':
      return (
        <Box g={g} x={5} y={2.8} w={14} h={18.4} r={3} style={{
          borderBottomLeftRadius: 7 * g.s + g.sw, borderBottomRightRadius: 7 * g.s + g.sw,
        }} />
      );
    case 'info':
      return (<>
        <Ring g={g} cx={12} cy={12} d={19} />
        <Dot g={g} cx={12} cy={8} d={2} />
        <Bar g={g} cx={12} cy={13.5} len={5} vertical />
      </>);
    case 'mail':
      return (<>
        <Box g={g} x={3.5} y={6} w={17} h={12} r={2.5} />
        {/* envelope flap: two slanted bars meeting at center */}
        <Bar g={g} cx={8.2} cy={9.8} len={8.4} deg={28} />
        <Bar g={g} cx={15.8} cy={9.8} len={8.4} deg={-28} />
      </>);
    case 'clock':
      return (<>
        <Ring g={g} cx={12} cy={12} d={19} />
        <Bar g={g} cx={12} cy={9.5} len={5} vertical />
        <Bar g={g} cx={13.8} cy={12} len={3.6} />
      </>);
    case 'calendar':
      return (<>
        <Box g={g} x={3.5} y={5} w={17} h={15.5} r={2} />
        <Bar g={g} cx={12} cy={9.8} len={17} />
        <Bar g={g} cx={8} cy={4.4} len={3.4} vertical />
        <Bar g={g} cx={16} cy={4.4} len={3.4} vertical />
      </>);
    case 'refresh':
      return (<>
        <Ring g={g} cx={12} cy={12} d={17} gapTop rotate={40} />
        <Bar g={g} cx={19.6} cy={7.2} len={4.6} deg={16} />
      </>);
    case 'package':
      return (<>
        <Box g={g} x={4} y={5} w={16} h={15.5} r={2} />
        <Bar g={g} cx={12} cy={9} len={8} vertical />
        <Dot g={g} cx={12} cy={14.5} d={2} />
      </>);
    case 'car':
      return (<>
        <Box g={g} x={7} y={6.5} w={10} h={6} r={2.5} style={{ borderBottomWidth: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }} />
        <Box g={g} x={3} y={10.5} w={18} h={6.2} r={2} />
        <Dot g={g} cx={7.5} cy={17.8} d={3.4} />
        <Dot g={g} cx={16.5} cy={17.8} d={3.4} />
      </>);
    case 'alert-triangle':
      return (<>
        <Bar g={g} cx={12} cy={19.4} len={16.6} />
        <Bar g={g} cx={7.8} cy={11.9} len={17} deg={-60} />
        <Bar g={g} cx={16.2} cy={11.9} len={17} deg={60} />
        <Bar g={g} cx={12} cy={11} len={4.4} vertical />
        <Dot g={g} cx={12} cy={16} d={2} />
      </>);
    case 'phone':
      return (<>
        <Box g={g} x={6.8} y={2.8} w={10.4} h={18.4} r={2.6} />
        <Dot g={g} cx={12} cy={17.6} d={2} />
      </>);
    case 'map-pin':
      return (<>
        <Ring g={g} cx={12} cy={9.5} d={11} />
        <Bar g={g} cx={10.2} cy={17} len={7} deg={64} />
        <Bar g={g} cx={13.8} cy={17} len={7} deg={-64} />
        <Dot g={g} cx={12} cy={9.5} d={2.6} />
      </>);
    case 'list':
      return (<>
        {[6, 12, 18].map((y) => (
          <React.Fragment key={y}>
            <Dot g={g} cx={4.6} cy={y} d={2.2} />
            <Bar g={g} cx={14} cy={y} len={12} />
          </React.Fragment>
        ))}
      </>);
    case 'more-horizontal':
      return (<>
        <Dot g={g} cx={5} cy={12} d={2.6} />
        <Dot g={g} cx={12} cy={12} d={2.6} />
        <Dot g={g} cx={19} cy={12} d={2.6} />
      </>);
    case 'download':
      return (<>
        <Bar g={g} cx={12} cy={7.5} len={9} vertical />
        <Bar g={g} cx={9.8} cy={10.1} len={6.2} deg={45} />
        <Bar g={g} cx={14.2} cy={10.1} len={6.2} deg={-45} />
        <Bar g={g} cx={12} cy={19.6} len={16} />
      </>);
    case 'chevron-left':
      return (<>
        <Bar g={g} cx={11.9} cy={8.6} len={9.6} deg={-46} />
        <Bar g={g} cx={11.9} cy={15.4} len={9.6} deg={46} />
      </>);
    case 'chevron-right':
      return (<>
        <Bar g={g} cx={12.1} cy={8.6} len={9.6} deg={46} />
        <Bar g={g} cx={12.1} cy={15.4} len={9.6} deg={-46} />
      </>);
    case 'copy':
      return (<>
        <Box g={g} x={9} y={3.5} w={11.5} h={11.5} r={2} />
        <Box g={g} x={3.5} y={9} w={11.5} h={11.5} r={2} />
      </>);
  }
}

export function Icon({ name, size = 20, color, style }: {
  name: IconName; size?: number; color: string; style?: StyleProp<ViewStyle>;
}) {
  const s = size / 24;
  const sw = Math.max(1.5, 1.75 * s);
  const g: G = { s, sw, c: color };
  return (
    <View style={[{ width: size, height: size }, style]} pointerEvents="none">
      {glyph(name, g)}
    </View>
  );
}
